import { SDLCMetadata } from "@sscs/types";
import { prettifyLargeCount } from "@sscs/utils";
import { format } from "date-fns";
import { ASSESSMENT_CATEGORY_ORDER } from "../../../constants";
/** String used to represent unknown component ages in timelines metadata */
export const TIMELINES_UNKNOWN_TIMESTAMP = "0";
/** Simple utility to check if a timestamp from timelines metadata is considered unknown */
export const isUnknownTimelinesTimestamp = (timestamp) => timestamp === TIMELINES_UNKNOWN_TIMESTAMP;
export const getYearFromTimestamp = (timestamp) => isUnknownTimelinesTimestamp(timestamp) ? timestamp : timestamp.slice(0, 4);
export const formatTimelineTimestamp = (timestamp) => {
    if (!timestamp || isUnknownTimelinesTimestamp(timestamp)) {
        return undefined;
    }
    const year = parseInt(timestamp.slice(0, 4));
    const month = parseInt(timestamp.slice(4, 6));
    if (isNaN(year) || isNaN(month)) {
        return undefined;
    }
    // zero indexed months in JS...
    return format(new Date(year, month - 1, 1), "MMMM yyyy");
};
/**
 * Calculates the timestamp (year and month) before which 50% of components were introduced.
 * If there are components with unknown age in the timeline data, those components aren't used in the calculation.
 *
 * @param timeline timeline data
 * @param componentsWithKnownAgeCount the total count of components with known age
 */
export const getHalfComponentsIntroducedBefore = (timeline, componentsWithKnownAgeCount) => {
    let halfComponentsIntroducedBeforeTimestamp = undefined;
    let componentCount = 0;
    for (const [timestamp, riskStatistics] of Object.entries(timeline)) {
        if (isUnknownTimelinesTimestamp(timestamp))
            continue;
        componentCount += riskStatistics.count;
        if (componentCount >= componentsWithKnownAgeCount / 2) {
            halfComponentsIntroducedBeforeTimestamp = timestamp;
            break;
        }
    }
    return formatTimelineTimestamp(halfComponentsIntroducedBeforeTimestamp);
};
export const getCounts = (riskStatistics) => {
    const riskCountsByCategory = ASSESSMENT_CATEGORY_ORDER.reduce((acc, category) => {
        const riskCountsByStatus = riskStatistics.metrics[SDLCMetadata.metrics_t[`risk_${category}_k`]];
        acc[category].total = riskCountsByStatus.reduce((countForCategory, countForCategoryForStatus) => countForCategory + countForCategoryForStatus, 0);
        acc[category].byStatus = riskCountsByStatus;
        return acc;
    }, {
        licenses: { total: 0, byStatus: [0, 0, 0] },
        secrets: { total: 0, byStatus: [0, 0, 0] },
        vulnerabilities: {
            total: 0,
            byStatus: [0, 0, 0],
        },
        hardening: { total: 0, byStatus: [0, 0, 0] },
        tampering: { total: 0, byStatus: [0, 0, 0] },
        malware: { total: 0, byStatus: [0, 0, 0] },
    });
    return {
        componentCount: riskStatistics.count,
        riskCounts: {
            byCategory: riskCountsByCategory,
        },
    };
};
const STATUS_ORDER = [
    "fail",
    "warning",
    "pass",
];
export const getMostSevereRiskStatus = (riskCountsByStatus) => {
    for (const status of STATUS_ORDER) {
        const count = riskCountsByStatus[SDLCMetadata.status_t[`status_${status}_k`]];
        if (count > 0)
            return { status, count };
    }
    return undefined;
};
export const getComponentCountsFromTimeline = (timeline) => Object.entries(timeline).reduce((acc, [timestamp, riskStatistics]) => {
    acc.totalComponentsCount += riskStatistics.count;
    if (!isUnknownTimelinesTimestamp(timestamp)) {
        acc.componentsWithKnownAgeCount += riskStatistics.count;
    }
    return acc;
}, {
    totalComponentsCount: 0,
    componentsWithKnownAgeCount: 0,
});
export const getTimelineDataByYear = (timelineDataByMonth) => {
    const timelineDataByYear = [];
    Object.entries(timelineDataByMonth).forEach(([timestamp, riskStatistics]) => {
        const year = getYearFromTimestamp(timestamp);
        const counts = getCounts(riskStatistics);
        // Timeline data should be grouped by a year
        const existingTimelineDataForYear = timelineDataByYear.find((timelineData) => timelineData.year === year);
        if (existingTimelineDataForYear) {
            existingTimelineDataForYear.componentCount += counts.componentCount;
            Object.keys(counts.riskCounts.byCategory).forEach((category) => {
                const existingRiskCountsForCategory = existingTimelineDataForYear.riskCounts.byCategory[category];
                const newRiskCountsForCategory = counts.riskCounts.byCategory[category];
                existingRiskCountsForCategory.total += newRiskCountsForCategory.total;
                existingRiskCountsForCategory.byStatus =
                    existingRiskCountsForCategory.byStatus.map((count, index) => count + newRiskCountsForCategory.byStatus[index]);
            });
        }
        else {
            timelineDataByYear.push({ year, ...counts });
        }
    });
    return timelineDataByYear;
};
export const componentAgeHasIssues = (riskCounts) => {
    for (const category of Object.keys(riskCounts.byCategory)) {
        if (riskCounts.byCategory[category].total > 0) {
            return true;
        }
    }
    return false;
};
export const formatComponentCount = (count) => {
    return count < 1000 ? count.toLocaleString() : prettifyLargeCount(count, 0);
};
//# sourceMappingURL=utils.js.map